import { OperationVariables, QueryHookOptions, TypedDocumentNode, useQuery } from '@apollo/client';

import { getApiSixClientId, getLocale } from '~helpers';
import { useCacheVersion } from '~providers';

type ApiGatewayQueryOptions<TData, TVariables extends OperationVariables> = Omit<
  QueryHookOptions<TData, TVariables>,
  'variables'
> & {
  variables?: Omit<TVariables, 'context'>;
};

function useApiGatewayQuery<
  TData = any,
  TVariables extends OperationVariables = OperationVariables,
>(
  query: TypedDocumentNode<TData, TVariables>,
  options?: ApiGatewayQueryOptions<TData, TVariables>,
) {
  const { context, variables, ...restOfOptions } = options ?? {
    context: undefined,
    variables: undefined,
  };
  const { headers, ...restOfContext } = context ?? { headers: {} };

  const clientId = getApiSixClientId();
  const language = getLocale();
  const cacheVersion = useCacheVersion();

  return useQuery<TData, TVariables>(query, {
    context: {
      headers: {
        'Accept-Language': language,
        'X-CLIENT': clientId,
        ...headers,
      },
      ...restOfContext,
    },
    variables: {
      ...variables,
      context: { clientId, language, cacheVersion },
    } as unknown as TVariables,

    ...restOfOptions,
  });
}

export default useApiGatewayQuery;
